/**
 * Cuánta IA le queda este mes, como anillo junto al botón de enviar: el mismo
 * que en el editor (`AiTokenMeter`), con el detalle al pasar por encima y el
 * atajo a ampliar el plan.
 */
import { IconArrowUp } from '@tabler/icons-react'
import type { AiTokenBudget } from '../../lib/api'
import { activeLanguage, useI18n } from '../../lib/i18n'

const RADIUS = 7
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

/** Ámbar pasados dos tercios, rojo al gastarse. */
function toneFor(percentUsed: number, exhausted: boolean) {
  if (exhausted || percentUsed >= 100) return 'is-spent'
  if (percentUsed >= 66) return 'is-warning'
  return 'is-ok'
}

/** La cuota se reinicia el primero de cada mes en UTC, como en el worker. */
export function nextReset(t: (key: string, vars?: Record<string, string | number>) => string) {
  const now = new Date()
  const reset = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
  const days = Math.max(0, Math.ceil((reset.getTime() - now.getTime()) / 86_400_000))
  return {
    /* En UTC, como el reinicio: en América el día 1 a medianoche UTC aún es el último del mes. */
    date: reset.toLocaleDateString(activeLanguage(), { day: 'numeric', month: 'long', timeZone: 'UTC' }),
    when: days <= 1 ? t('ai.tomorrow') : t('ai.inDays', { count: days }),
  }
}

export function AssistantAiMeter({ budget, onOpenPlans }: { budget: AiTokenBudget; onOpenPlans: () => void }) {
  const { t } = useI18n()
  const unlimited = budget.limit === null
  const percent = unlimited ? 0 : Math.min(100, Math.max(0, Math.round(budget.percentUsed)))
  const remaining = Math.max(0, 100 - percent)
  const tone = unlimited ? 'is-unlimited' : toneFor(percent, budget.exhausted)
  const reset = nextReset(t)
  const label = unlimited ? t('ai.usageUnlimited') : t('ai.meter.aria', { pct: remaining })

  return (
    /* `onClick` parado: el formulario de la caja enfoca el texto al pulsar en
       cualquier parte, y aquí se pulsa para leer o ampliar, no para escribir. */
    <button type="button" className={`dash-assist-meter ${tone}`} aria-label={label} onClick={(event) => event.stopPropagation()}>
      <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false">
        <circle className="dash-assist-meter-track" cx="9" cy="9" r={RADIUS} />
        {unlimited ? null : (
          <circle
            className="dash-assist-meter-value"
            cx="9"
            cy="9"
            r={RADIUS}
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={CIRCUMFERENCE * (1 - percent / 100)}
          />
        )}
      </svg>
      <span className="dash-assist-meter-popover" role="tooltip">
        <span className="dash-assist-meter-title">{t('ai.meter.title')}</span>
        {unlimited ? (
          <span className="dash-assist-meter-figure"><b>∞</b><span>{t('ai.meter.unlimited')}</span></span>
        ) : (
          <>
            <span className="dash-assist-meter-figure"><b>{remaining}%</b><span>{t('ai.meter.left')}</span></span>
            <span className="dash-assist-meter-bar" aria-hidden="true"><i style={{ width: `${remaining}%` }} /></span>
            <small>{t('ai.meter.resets', { date: reset.date, when: reset.when })}</small>
            <span
              role="button"
              tabIndex={0}
              className="dash-assist-meter-plans"
              onClick={(event) => { event.stopPropagation(); onOpenPlans() }}
              onKeyDown={(event) => { if (event.key === 'Enter') onOpenPlans() }}
            >
              <IconArrowUp size={12} aria-hidden="true" />
              {t('ai.expandPlan')}
            </span>
          </>
        )}
      </span>
    </button>
  )
}
